/**
 * memo-context (design-20260724-rightrail-memo §4.8) — 현재 라우트 → 메모 작성 화면 컨텍스트.
 *
 * 메모를 **어느 메뉴 화면에서 썼는지** 기록하고, 그 화면에서 패널을 열면 해당 메모를 위로 올린다(§4.9).
 *
 * 매칭 단위 = **경로 첫 세그먼트**(결정 D-7):
 *   '/project/teams' · '/project/stats' → 모두 'project'
 *   사용자가 인지하는 단위는 라우트가 아니라 '메뉴 화면'이라, 드릴다운 하위 화면에서 쓴 메모도
 *   상위 메뉴에서 잡혀야 자연스럽다.
 *
 * ⚠ 메뉴 레지스트리(admin_menu_registry)의 menu_key 로 매칭하지 않는 이유:
 *   운영 중 url 이 바뀌면(실제 2026-07-24 'settings' 행이 /me → /settings) 과거 메모의 매칭이
 *   통째로 끊긴다. 경로 첫 세그먼트는 레지스트리와 독립이라 그런 변경에 영향받지 않는다.
 */
import { tLayout, type LayoutI18nKey } from './layout-i18n';

export interface MemoContext {
  /** 매칭·정렬용 정규화 키. 없으면 null(컨텍스트 없는 메모). */
  contextKey: string | null;
  /** 배지 클릭 시 복귀할 전체 경로. */
  contextPath: string | null;
  /** 배지 표시용 화면명. 해석 실패 시 null → 배지 미표시(폴백 안전). */
  contextLabel: string | null;
}

/** 첫 세그먼트 → layout.nav.* 키. 미등록 경로는 라벨 없이 키만 남는다(배지 미표시). */
const LABEL_KEY_BY_SEGMENT: Record<string, LayoutI18nKey> = {
  dashboard: 'layout.nav.dashboard',
  chat: 'layout.nav.chat',
  mail: 'layout.nav.mail',
  calendar: 'layout.nav.calendar',
  // 2026-07-24 — /task 는 '프로젝트' 메뉴가 직접 가리키는 화면이 됐다(내 프로젝트).
  // 사용자가 인지하는 화면명 기준이라 배지도 '프로젝트' 로 읽혀야 한다(키 'task' 는 그대로 → 기존 메모 매칭 유지).
  task: 'layout.nav.project',
  project: 'layout.nav.project',
  workspace: 'layout.nav.workspace',
  info: 'layout.nav.info',
  board: 'layout.nav.board',
  organization: 'layout.nav.organization',
  settings: 'layout.nav.settings_integrated',
  me: 'layout.nav.profile',
  'ai-minutes': 'layout.nav.ai_minutes',
  'system-admin': 'layout.nav.system_admin',
};

/**
 * 경로 → 정규화 키. 서버(normalizeContextKey)와 같은 규칙 —
 * 첫 세그먼트 소문자, 허용 문자(a-z0-9-_) 40자 이내가 아니면 null.
 */
export function memoContextKeyOf(pathname: string): string | null {
  const first = pathname.replace(/^\/+/, '').split('/')[0]?.trim().toLowerCase() ?? '';
  if (first === '') return null;
  return /^[a-z0-9_-]{1,40}$/.test(first) ? first : null;
}

/** 정규화 키 → 화면명. 미등록이면 null(배지를 그리지 않는다). */
export function memoContextLabelOf(key: string | null): string | null {
  if (!key) return null;
  const labelKey = LABEL_KEY_BY_SEGMENT[key];
  return labelKey ? tLayout(labelKey) : null;
}

/**
 * 현재 라우트 → 메모에 기록할 컨텍스트 3종.
 * contextPath 는 전체 경로 그대로(드릴다운 화면으로 정확히 복귀하기 위함).
 */
export function memoContextOf(pathname: string): MemoContext {
  const contextKey = memoContextKeyOf(pathname);
  return {
    contextKey,
    contextPath: contextKey ? pathname : null,
    contextLabel: memoContextLabelOf(contextKey),
  };
}
